"use client";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { addToShoppingCart } from './shoppingcart.functions';
import { AddToCartButton, ViewItemDetails } from './shoppingcart.components';
import { usePixelatedConfig } from '../config/config.client';
import { Callout } from '../structure/callout';
import { PageSection, PageGridItem, PageTitleHeader, PageSectionHeader } from '../structure/page-blocks';
import { SmartImage } from '../elements/smartimage';
import { Carousel } from '../structure/carousel';
import { buildSquareStoreFilters, matchesSquareStorePriceRange, SquarePaymentError, getSquarePaymentErrorMessage, SquareStoreItemShape } from './square';
import { ProductSchema, SchemaEvent } from '../foundation/schema';
import { getPolicyRouteUrl } from '../foundation/schema.functions';
import { sanitizeString, normalizeEmail } from '../foundation/utilities';
import "./square.css";
function buildSquareProductSchema(item, siteInfo, policyUrl) {
    const images = item.itemImageURLs?.filter((image) => typeof image === 'string') ?? [];
    const productImages = images.length > 0 ? images : item.itemImageURL ? [item.itemImageURL] : undefined;
    const brandName = siteInfo?.brand?.name || siteInfo?.name || undefined;
    const sku = item.itemSKU || item.itemID;
    const mpn = item.properties?.mpn || sku;
    const rawGtin = sanitizeString(item.properties?.gtin);
    const gtin = rawGtin && /^(?:\d{8}|\d{12}|\d{13}|\d{14})$/.test(rawGtin) ? rawGtin : undefined;
    return {
        name: item.itemTitle,
        description: item.itemDescription || item.itemTitle,
        image: productImages,
        url: item.itemURL,
        sku,
        brand: brandName ? { name: brandName } : undefined,
        mpn,
        ...(gtin && { gtin }),
        shippingDetails: {
            isShippable: item.itemIsShippable !== false,
            weight: item.itemWeight,
            weightUnit: item.itemWeightUnit,
        },
        ...(policyUrl ? { hasMerchantReturnPolicy: policyUrl } : {}),
        offers: {
            url: item.itemURL,
            priceCurrency: item.itemCurrency ?? 'USD',
            price: item.itemPrice,
            availability: item.itemInventory > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
        },
    };
}
function buildSquareEventSchema(item) {
    const images = item.itemImageURLs?.filter((image) => typeof image === 'string') ?? [];
    const eventImages = images.length > 0 ? images : item.itemImageURL ? [item.itemImageURL] : [];
    const startDate = item.itemStartDate && item.itemStartTime ? buildSquareEventIsoDate(`${item.itemStartDate} ${item.itemStartTime}`) : undefined;
    const endDate = item.itemEndDate && item.itemEndTime ? buildSquareEventIsoDate(`${item.itemEndDate} ${item.itemEndTime}`) : undefined;
    return {
        fields: {
            id: item.itemID,
            title: item.itemTitle,
            description: item.itemDescription || item.itemTitle,
            startDate,
            endDate,
            carouselImages: eventImages.length > 0 ? eventImages.map((image) => ({ image })) : undefined,
            price: item.itemPrice,
            maxSeats: item.itemInventory,
        },
    };
}
function isEventComplete(item) {
    if (!item.itemEndDate || !item.itemEndTime)
        return false;
    const endDateTime = buildSquareEventIsoDate(`${item.itemEndDate} ${item.itemEndTime}`);
    if (!endDateTime)
        return false;
    const endTime = Date.parse(endDateTime);
    return !Number.isNaN(endTime) && endTime < Date.now();
}
const SQUARE_PRODUCTION_SCRIPT_URL = 'https://web.squarecdn.com/v1/square.js';
const SQUARE_SANDBOX_SCRIPT_URL = 'https://sandbox.web.squarecdn.com/v1/square.js';
function getSquareScriptUrl(applicationId, squareConfig, checkoutData) {
    if (squareConfig) {
        const checkoutEmail = normalizeEmail(checkoutData?.shippingTo?.email);
        const sandboxEmails = Array.isArray(squareConfig?.sandboxSquareEmails)
            ? squareConfig.sandboxSquareEmails.map((value) => normalizeEmail(value))
            : [];
        const explicitSandbox = squareConfig?.environment === 'sandbox';
        const useSandbox = explicitSandbox || Boolean(checkoutEmail && sandboxEmails.includes(checkoutEmail));
        return useSandbox
            ? squareConfig?.sandboxSquareScriptUrl || SQUARE_SANDBOX_SCRIPT_URL
            : squareConfig?.squareScriptUrl || SQUARE_PRODUCTION_SCRIPT_URL;
    }
    return applicationId?.startsWith('sandbox-')
        ? SQUARE_SANDBOX_SCRIPT_URL
        : SQUARE_PRODUCTION_SCRIPT_URL;
}
function buildSquareEventIsoDate(value) {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
}
function loadSquareScript(src) {
    return new Promise((resolve, reject) => {
        const scripts = document.querySelectorAll('script[src]');
        let scriptAlreadyLoaded = false;
        for (let i = 0; i < scripts.length; i++) {
            if (scripts[i].src.includes(src)) {
                scriptAlreadyLoaded = true;
                break;
            }
        }
        if (scriptAlreadyLoaded && window.Square) {
            resolve();
            return;
        }
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.onload = () => {
            resolve();
        };
        script.onerror = () => {
            reject(new Error('Failed to load Square Payments SDK.'));
        };
        document.head.appendChild(script);
    });
}
/**
 * SquareCheckout component renders a Square payment form for the shopping cart. It requires the Square application ID, location ID, checkout data, and an onApprove callback function as props. The component will load the Square Payments SDK, render the card input form, and handle the payment process when the user clicks the pay button.
 *
 * @param: applicationId - The application ID for the Square application, used to authenticate API requests.
 * @param: locationId - The location ID for the Square account, used to specify the location for the transaction.
 * @param: checkoutData - An object containing the details of the checkout, such as total amount, currency, and shipping information.
 * @param: onApprove - A callback function that will be called when the payment is approved by the user. It receives the payment details as an argument.
 * @returns: A React component that renders the Square payment form and handles the payment process.
 */
SquareCheckout.propTypes = {
    applicationId: PropTypes.string.isRequired,
    locationId: PropTypes.string.isRequired,
    checkoutData: PropTypes.object.isRequired,
    onApprove: PropTypes.func.isRequired,
    onSquarePaymentCapture: PropTypes.func,
};
export function SquareCheckout(props) {
    const config = usePixelatedConfig();
    const squareConfig = config?.integrations?.square;
    const [card, setCard] = useState(null);
    const [initialized, setInitialized] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    useEffect(() => {
        let active = true;
        async function initializeSquare() {
            if (!props.applicationId || !props.locationId) {
                return;
            }
            try {
                const squareScriptUrl = getSquareScriptUrl(props.applicationId, squareConfig, props.checkoutData);
                await loadSquareScript(squareScriptUrl);
                if (!active)
                    return;
                const Square = window.Square;
                if (!Square || typeof Square.payments !== 'function') {
                    throw new Error('Square Payments SDK failed to initialize.');
                }
                const payments = await Square.payments(props.applicationId, props.locationId);
                const cardInput = await payments.card();
                await cardInput.attach('#square-card-container');
                if (!active)
                    return;
                setCard(cardInput);
                setInitialized(true);
            }
            catch (error) {
                if (!active)
                    return;
                setErrorMessage(error?.message || 'Unable to initialize Square payment.');
            }
        }
        initializeSquare();
        return () => {
            active = false;
        };
    }, [props.applicationId, props.locationId, props.checkoutData, squareConfig]);
    async function handleSquarePayment(event) {
        event.preventDefault();
        setErrorMessage(null);
        if (!card) {
            setErrorMessage('Square payment form is not ready yet.');
            return;
        }
        try {
            setIsProcessing(true);
            const result = await card.tokenize();
            if (result.status === 'OK') {
                let captureResponse = undefined;
                if (typeof props.onSquarePaymentCapture === 'function') {
                    try {
                        captureResponse = await props.onSquarePaymentCapture({
                            sourceId: result.token,
                            checkoutData: props.checkoutData,
                            card: result,
                        });
                    }
                    catch (error) {
                        if (error instanceof SquarePaymentError) {
                            setErrorMessage(error.userMessage);
                            return;
                        }
                        setErrorMessage(getSquarePaymentErrorMessage(error) || error?.message || 'Square payment capture failed.');
                        return;
                    }
                }
                props.onApprove({
                    data: {
                        sourceId: result.token,
                        card: result,
                        checkoutData: props.checkoutData,
                        captureResponse,
                    },
                });
            }
            else {
                const errors = result.errors?.map((item) => item.message).join(', ') || 'Square tokenization failed.';
                setErrorMessage(errors);
            }
        }
        catch (error) {
            if (error instanceof SquarePaymentError) {
                setErrorMessage(error.userMessage);
                return;
            }
            setErrorMessage(getSquarePaymentErrorMessage(error) || error?.message || 'Square payment capture failed.');
        }
        finally {
            setIsProcessing(false);
        }
    }
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "pix-cart-payment-method", children: [_jsx("div", { id: "square-card-container", className: "square-card-container" }), errorMessage && _jsx("div", { className: "pix-cart-error", children: errorMessage }), _jsx("button", { className: "pix-cart-button", type: "button", onClick: handleSquarePayment, disabled: !initialized || isProcessing, children: isProcessing ? 'Processing payment...' : initialized ? 'Pay with Square' : 'Loading Square...' })] }), _jsx("br", {}), _jsx("br", {})] }));
}
/**
 *
 * SquareStoreListFilter component renders a filter form for the Square store item listing. It allows users to select a property and value to filter the displayed items. The component receives an array of filters (each with a name and possible values) and a callback function as props. When the user selects a property and value and clicks the filter button, the callback function is called with the selected filter values.
 *
 * @param: filters - An array of filter objects, each containing a name and an array of possible values for that property.
 * @param: callback - A function that will be called when the user applies a filter. It receives an object with the selected property name and value.
 *
 * @returns: A React component that renders the filter form and handles user input to apply filters to the Square store item listing.
 *
 */
SquareStoreListFilter.propTypes = {
    filters: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string.isRequired,
        values: PropTypes.arrayOf(PropTypes.shape({
            label: PropTypes.string.isRequired,
            value: PropTypes.string.isRequired,
        })).isRequired,
    })).isRequired,
    callback: PropTypes.func.isRequired,
    clearCallback: PropTypes.func,
};
export function SquareStoreListFilter(props) {
    const [selectedProperty, setSelectedProperty] = useState('');
    const [selectedValue, setSelectedValue] = useState('');
    const [availableValues, setAvailableValues] = useState([]);
    useEffect(() => {
        if (!selectedProperty) {
            setAvailableValues([]);
            setSelectedValue('');
            return;
        }
        const selectedFilter = props.filters.find((filter) => Boolean(filter && filter.name === selectedProperty));
        setAvailableValues(selectedFilter?.values || []);
        setSelectedValue('');
    }, [selectedProperty, props.filters]);
    function handlePropertyChange(event) {
        setSelectedProperty(event.target.value);
    }
    function handleValueChange(event) {
        setSelectedValue(event.target.value);
    }
    function applyFilter() {
        if (selectedProperty && selectedValue) {
            props.callback({ propertyName: selectedProperty, propertyValue: selectedValue });
        }
    }
    function clearFilter() {
        setSelectedProperty('');
        setSelectedValue('');
        setAvailableValues([]);
        if (typeof props.clearCallback === 'function') {
            props.clearCallback();
        }
    }
    return (_jsxs("form", { className: "square-store-filter", name: "square-store-filter", id: "square-store-filter", onSubmit: (e) => { e.preventDefault(); applyFilter(); }, children: [_jsxs("span", { className: "filter-input", children: [_jsx("label", { htmlFor: "square-property", children: "Property" }), _jsxs("select", { id: "square-property", value: selectedProperty, onChange: handlePropertyChange, children: [_jsx("option", { value: "" }), props.filters.filter((filter) => Boolean(filter)).map((filter) => (_jsx("option", { value: filter.name, children: filter.name }, filter.name)))] })] }), _jsxs("span", { className: "filter-input", children: [_jsx("label", { htmlFor: "square-value", children: "Value" }), _jsxs("select", { id: "square-value", value: selectedValue, onChange: handleValueChange, disabled: !selectedProperty, children: [_jsx("option", { value: "" }), availableValues.map((filterValue) => (_jsx("option", { value: filterValue.value, children: filterValue.label }, filterValue.value)))] })] }), _jsx("span", { className: "filter-input", children: _jsx("button", { type: "button", onClick: applyFilter, disabled: !selectedProperty || !selectedValue, children: "Filter" }) }), _jsx("span", { className: "filter-input", children: _jsx("button", { type: "button", onClick: clearFilter, children: "Clear" }) })] }));
}
/**
 * SquareStoreItemSmall component renders a single item in the Square store listing. It displays the item's image, title, price, inventory status, description, and properties. The component also includes buttons to view item details and add the item to the shopping cart. It receives an item object as a prop, which contains all the necessary information about the store item.
 * @param: item - An object representing the Square store item, containing properties such as itemID, itemTitle, itemPrice, itemCurrency, itemInventory, itemDescription, itemImageURL, and any additional properties.
 * @return: A React component that displays the store item information and provides actions for viewing details and adding to cart.
 */
SquareStoreItemSmall.propTypes = {
    item: PropTypes.shape(SquareStoreItemShape).isRequired,
    itemURLPrefix: PropTypes.string,
};
export function SquareStoreItemSmall(props) {
    const itemURL = props.itemURLPrefix && props.item.itemURL?.startsWith('/store/')
        ? `${props.itemURLPrefix.replace(/\/$/, '')}/${props.item.itemURL.replace(/^\/store\//, '')}`
        : props.item.itemURL;
    return (_jsx("div", { className: "square-store-item-callout", children: _jsx(Callout, { variant: "boxed", layout: "vertical", direction: "left", img: props.item.itemImageURL ?? 'https://www.pixelated.tech/images/icons/placeholder.png', imgAlt: props.item.itemTitle, title: props.item.itemTitle, url: itemURL, 
            // subtitle={`${props.item.itemCurrency ?? 'USD'} ${props.item.itemPrice.toFixed(2)} · In stock: ${props.item.itemInventory}`}
            subtitle: `${props.item.itemCurrency ?? 'USD'} ${props.item.itemPrice.toFixed(2)}`, content: props.item.itemDescription || 'No description available.', children: _jsx("div", { className: "square-store-item-card-body", children: _jsxs("div", { className: "square-store-item-actions", children: [_jsx(ViewItemDetails, { href: itemURL, itemID: props.item.itemID }), !isEventComplete(props.item) ? (_jsx(AddToCartButton, { handler: addToShoppingCart, item: {
                                ...props.item,
                                itemQuantity: 1,
                            }, itemID: props.item.itemID })) : null] }) }) }) }));
}
/**
 * SquareStoreItemSmall component renders a single item in the Square store listing. It displays the item's image, title, price, inventory status, description, and properties. The component also includes buttons to view item details and add the item to the shopping cart. It receives an item object as a prop, which contains all the necessary information about the store item.
 * @param: item - An object representing the Square store item, containing properties such as itemID, itemTitle, itemPrice, itemCurrency, itemInventory, itemDescription, itemImageURL, and any additional properties.
 * @return: A React component that displays the store item information and provides actions for viewing details and adding to cart.
 */
SquareStoreItemLarge.propTypes = {
    item: PropTypes.shape(SquareStoreItemShape).isRequired,
    itemURLPrefix: PropTypes.string,
};
export function SquareStoreItemLarge(props) {
    const itemURL = props.itemURLPrefix && props.item.itemURL?.startsWith('/store/')
        ? `${props.itemURLPrefix.replace(/\/$/, '')}/${props.item.itemURL.replace(/^\/store\//, '')}`
        : props.item.itemURL;
    return (_jsx("div", { className: "square-store-item-callout", children: _jsx(Callout, { variant: "grid", layout: "horizontal", direction: "left", img: props.item.itemImageURL ?? 'https://www.pixelated.tech/images/icons/placeholder.png', imgAlt: props.item.itemTitle, title: props.item.itemTitle, url: itemURL, subtitle: `${props.item.itemStartDate} ${props.item.itemStartTime} - ${props.item.itemEndDate} ${props.item.itemEndTime}`, content: props.item.itemDescription || 'No description available.', buttonText: "More Details", children: _jsxs("div", { className: "square-store-item-card-body", children: [_jsx("div", { className: "square-store-item-description", children: props.item.itemDescription || 'No description available.' }), _jsxs("div", { className: "square-store-item-actions", children: [_jsx(ViewItemDetails, { href: itemURL, itemID: props.item.itemID }), !isEventComplete(props.item) ? (_jsx(AddToCartButton, { handler: addToShoppingCart, item: {
                                    ...props.item,
                                    itemQuantity: 1,
                                }, itemID: props.item.itemID })) : null] })] }) }) }));
}
/**
 *
 * SquareStoreItems component renders a list of items in the Square store with optional filtering. It displays a header with a title and introduction, a filter form if there are filterable properties, and a grid of SquareStoreItem components for each item in the list. The component receives an array of items, an optional title, and an optional introduction as props. It allows users to filter the displayed items based on their properties using the SquareStoreListFilter component.
 * @param: items - An array of objects representing the Square store items to be displayed, each containing properties such as itemID, itemTitle, itemPrice, itemCurrency, itemInventory, itemDescription, itemImageURL, and any additional properties.
 * @param: title - An optional string to be displayed as the header title for the store items section.
 * @param: intro - An optional string to be displayed as an introduction or description below the header title.
 *
 * @return: A React component that displays a list of Square store items with filtering options and actions for each item.
 *
 */
SquareStoreItems.propTypes = {
    items: PropTypes.arrayOf(PropTypes.shape(SquareStoreItemShape).isRequired).isRequired, // Array of Square store items to render
    filters: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string.isRequired,
        values: PropTypes.arrayOf(PropTypes.shape({
            label: PropTypes.string.isRequired,
            value: PropTypes.string.isRequired,
        })).isRequired,
    })),
    title: PropTypes.string, // Optional heading for the store listing
    intro: PropTypes.string, // Optional introductory text below the header title
    errorMessage: PropTypes.string, // Optional override for empty state copy
    emptyMessage: PropTypes.string, // Optional override for empty state copy
    initialFilter: PropTypes.shape({
        propertyName: PropTypes.string.isRequired,
        propertyValue: PropTypes.string.isRequired,
    }),
    showFilters: PropTypes.bool,
    showPastEvents: PropTypes.bool,
    itemSize: PropTypes.oneOf(['small', 'large']),
    itemURLPrefix: PropTypes.string,
};
export function SquareStoreItems(props) {
    const config = usePixelatedConfig();
    const policyUrl = getPolicyRouteUrl(config?.routes, config?.siteInfo);
    const items = props.items || [];
    const visibleItems = props.showPastEvents ? items : items.filter((item) => item.itemType?.toUpperCase() !== 'EVENT' || !isEventComplete(item));
    const filters = props.filters && props.filters.length ? props.filters : buildSquareStoreFilters(visibleItems);
    const showFilters = props.showFilters !== false;
    const itemSize = props.itemSize ?? 'small';
    const [selectedFilter, setSelectedFilter] = useState(props.initialFilter ?? null);
    const filteredItems = useMemo(() => {
        if (!selectedFilter || !selectedFilter.propertyName || !selectedFilter.propertyValue) {
            return visibleItems;
        }
        return visibleItems.filter((item) => {
            if (selectedFilter.propertyName === 'Category') {
                return Boolean(item.categories?.some((category) => category?.id === selectedFilter.propertyValue));
            }
            if (selectedFilter.propertyName === 'Price Range') {
                return matchesSquareStorePriceRange(item.itemPrice, selectedFilter.propertyValue);
            }
            return item.properties?.[selectedFilter.propertyName] === selectedFilter.propertyValue;
        });
    }, [visibleItems, selectedFilter]);
    const totalItems = visibleItems.length;
    const hasActiveFilter = Boolean(selectedFilter?.propertyName && selectedFilter?.propertyValue);
    function handleFilter(select) {
        setSelectedFilter(select);
    }
    return (_jsxs(_Fragment, { children: [_jsxs(PageGridItem, { columnStart: 1, columnEnd: -1, id: "square-store-header", className: "square-store-header", children: [_jsx(PageSectionHeader, { children: props.title ?? 'Boutique Store Items' }), _jsxs("div", { className: "square-store-item-count", children: ["Total items: ", totalItems, hasActiveFilter ? ` · Filtered items: ${filteredItems.length}` : ''] }), props.intro ? _jsx("p", { children: props.intro }) : null] }), showFilters ? (_jsx(PageGridItem, { columnStart: 1, columnEnd: -1, id: "square-store-filters", className: "square-store-filters", children: filters.length > 0 ? (_jsx(SquareStoreListFilter, { filters: filters, callback: handleFilter, clearCallback: () => setSelectedFilter(null) })) : (_jsx("div", { className: "square-store-filter-empty", children: "No filterable product details are available for these items." })) })) : null, filteredItems.map((item) => {
                return item.itemType?.toUpperCase() === 'EVENT' ? (_jsx(SchemaEvent, { event: buildSquareEventSchema(item) }, `square-schema-${item.itemID}`)) : (_jsx(ProductSchema, { product: buildSquareProductSchema(item, config?.siteInfo, policyUrl) }, `square-schema-${item.itemID}`));
            }), filteredItems.map((item) => (itemSize === 'large' ? (_jsx(PageGridItem, { columnStart: 1, columnEnd: -1, children: _jsx(SquareStoreItemLarge, { item: item, itemURLPrefix: props.itemURLPrefix }) }, item.itemID)) : (_jsx(PageGridItem, { children: _jsx(SquareStoreItemSmall, { item: item, itemURLPrefix: props.itemURLPrefix }) }, item.itemID))))] }));
}
/**
 * Props for the SquareFeaturedItems component.
 *
 * @prop items - An array of Square store items to be featured, each containing properties such as itemID, itemTitle, itemPrice, itemCurrency, itemInventory, itemDescription, itemImageURL, and any additional properties.
 * @prop title - An optional string to be displayed as the header title for the featured items section.
 * @prop intro - An optional string to be displayed as an introduction or description below the header title for the featured items section.
 *
 * @return A React component that displays a list of featured Square store items with a header and introduction.
 *
 */
SquareFeaturedItems.propTypes = {
    items: PropTypes.arrayOf(PropTypes.shape(SquareStoreItemShape).isRequired).isRequired, // Array of featured Square store items
    title: PropTypes.string, // Optional heading for the featured items section
    intro: PropTypes.string, // Optional introductory text for featured items
};
export function SquareFeaturedItems(props) {
    const config = usePixelatedConfig();
    const policyUrl = getPolicyRouteUrl(config?.routes, config?.siteInfo);
    return (_jsxs(_Fragment, { children: [_jsxs(PageGridItem, { columnStart: 1, columnEnd: -1, id: "square-featured-items-header", className: "square-featured-items-header", children: [_jsx(PageSectionHeader, { children: props.title ?? 'Featured Boutique Items' }), props.intro ? _jsx("p", { children: props.intro }) : null] }), props.items && props.items.length > 0 ? (_jsxs(_Fragment, { children: [props.items.map((item) => (_jsx(ProductSchema, { product: buildSquareProductSchema(item, config?.siteInfo, policyUrl) }, `featured-schema-${item.itemID}`))), props.items.map((item) => (_jsx(PageGridItem, { children: _jsx(SquareStoreItemSmall, { item: item }, item.itemID) }, item.itemID)))] })) : (_jsx("div", { className: "square-store-empty", children: "No featured boutique items are available right now." }))] }));
}
/**
 * SquareStoreItemDetail component renders detailed information about a Square store item, including structured data for the product, a page title header, and a page section with product details. It receives a Square store item object as a prop and builds the product schema using the provided item data. The component displays the product images, title, description, inventory, SKU, shipping information, weight, and start date/time if available. It also provides an action to add the item to the shopping cart.
 * @param: item - An object representing the Square store item, containing properties such as itemTitle, itemDescription, itemID, itemInventory, itemSKU, itemIsShippable, itemWeight, itemWeightUnit, itemStartDate, itemStartTime, and any additional item details.
 * @return: A React component that displays detailed information about a Square store item and provides an action to add the item to the shopping cart.
 *
 */
SquareStoreItemDetail.propTypes = {
    item: PropTypes.shape(SquareStoreItemShape).isRequired, // Square item object with details for the item detail page
};
export function SquareStoreItemDetail(props) {
    const config = usePixelatedConfig();
    const itemImageURLs = props.item.itemImageURLs?.filter((image) => typeof image === 'string') ?? [];
    const imageCards = itemImageURLs.length > 1 ? itemImageURLs.map((image, index) => ({
        index,
        cardIndex: index,
        cardLength: itemImageURLs.length,
        image,
        imageAlt: `${props.item.itemTitle} ${index + 1}`,
        imgFit: 'contain',
    })) : [];
    return (_jsxs(PageSection, { columns: 1, id: "square-store-item-detail", className: "square-store-item-detail", children: [props.item.itemType?.toUpperCase() === 'EVENT' ? (_jsx(SchemaEvent, { event: buildSquareEventSchema(props.item) })) : (_jsx(ProductSchema, { product: buildSquareProductSchema(props.item, config?.siteInfo, getPolicyRouteUrl(config?.routes, config?.siteInfo)) })), _jsxs("div", { className: "square-store-item-detail", children: [_jsx("div", { className: "square-store-item-detail-title", children: _jsx(PageTitleHeader, { children: props.item.itemTitle }) }), _jsx("div", { className: "square-store-item-detail-image", children: imageCards.length > 1 ? (_jsx(Carousel, { cards: imageCards, imgFit: "contain", draggable: false })) : itemImageURLs.length === 1 ? (_jsx(SmartImage, { aboveFold: true, src: itemImageURLs[0], alt: props.item.itemTitle })) : props.item.itemImageURL ? (_jsx(SmartImage, { aboveFold: true, src: props.item.itemImageURL, alt: props.item.itemTitle })) : (_jsx(SmartImage, { aboveFold: true, src: "https://www.pixelated.tech/images/icons/placeholder.png", alt: props.item.itemTitle })) }), _jsxs("div", { className: "square-store-item-detail-body", children: [_jsx("h3", { children: "Product Details" }), props.item.itemDescription ? _jsx("p", { children: props.item.itemDescription }) : null, _jsxs("div", { className: "square-store-item-detail-id", children: ["Item ID: ", props.item.itemID] }), _jsxs("div", { className: "square-store-item-detail-sku", children: ["SKU: ", props.item.itemSKU ?? 'N/A'] }), _jsxs("div", { className: "square-store-item-detail-inventory", children: ["In stock: ", props.item.itemInventory] }), _jsxs("div", { className: "square-store-item-detail-shipping", children: ["Shippable: ", props.item.itemIsShippable ? 'Yes' : 'No'] }), props.item.itemWeight != null ? (_jsxs("div", { className: "square-store-item-detail-weight", children: ["Weight: ", props.item.itemWeight.toFixed(2), " ", props.item.itemWeightUnit ?? 'lb'] })) : null, props.item.categories && props.item.categories.length > 0 ? (_jsxs("div", { className: "square-store-item-detail-categories", children: ["Categories: ", props.item.categories.filter((category) => Boolean(category)).map((category) => category.name).join(', ')] })) : null, props.item.itemStartDate && props.item.itemStartTime ? (_jsxs("div", { className: "square-store-item-detail-start", children: ["Start: ", props.item.itemStartDate, " at ", props.item.itemStartTime] })) : null, props.item.itemEndDate && props.item.itemEndTime ? (_jsxs("div", { className: "square-store-item-detail-end", children: ["End: ", props.item.itemEndDate, " at ", props.item.itemEndTime] })) : null, props.item.itemDurationHours != null ? (_jsxs("div", { className: "square-store-item-detail-duration", children: ["Duration: ", props.item.itemDurationHours, " hours"] })) : null, props.item.itemAvailableSeats != null ? (_jsxs("div", { className: "square-store-item-detail-seats", children: ["Available seats: ", props.item.itemAvailableSeats, props.item.itemMaxSeats != null ? ` · Max seats: ${props.item.itemMaxSeats}` : ''] })) : null, _jsx("div", { className: "square-store-item-detail-price", children: `${props.item.itemPrice.toFixed(2)} ${props.item.itemCurrency}` }), _jsx("div", { className: "square-store-item-detail-actions", children: props.item.itemType?.toUpperCase() === 'EVENT' && isEventComplete(props.item) ? (_jsx("div", { className: "square-store-item-detail-closed-message", children: "You cannot register for this event, because it is now over." })) : (_jsx(AddToCartButton, { handler: addToShoppingCart, item: {
                                        ...props.item,
                                        itemQuantity: 1,
                                    }, itemID: props.item.itemID })) })] })] })] }));
}
/**
 * SquareThankYou component renders a thank you message and order details after a successful Square payment. It receives the order data and configuration as props, extracts relevant information such as the payment token, amount, and currency, and displays them in a user-friendly format. If the necessary information is not available, it will display the raw order data for debugging purposes.
 * @param: orderData - An object containing the details of the completed order, including payment information and checkout data.
 * @param: config - An optional configuration object that may contain settings related to the shopping cart and currency.
 * @return: A React component that displays a thank you message and relevant order details after a successful Square payment.
 *
 */
renderSquareThankYou.propTypes = {
    orderData: PropTypes.any.isRequired, // The order data object containing payment and checkout details
    config: PropTypes.object, // Optional configuration object for additional settings
};
export function renderSquareThankYou(props) {
    const orderData = props.orderData;
    const config = props.config;
    const sourceId = orderData?.data?.sourceId || orderData?.sourceId;
    const amount = orderData?.data?.checkoutData?.total ?? orderData?.checkoutData?.total;
    const currency = config?.integrations?.shoppingcart?.currency || orderData?.data?.checkoutData?.currency || 'USD';
    return (_jsxs("div", { children: [_jsx("h3", { children: "Thank you for your payment!" }), sourceId ? (_jsxs(_Fragment, { children: ["Payment Token : ", sourceId, " ", _jsx("br", {}), "Amount : $", amount ?? 'Unknown', " ", currency, " ", _jsx("br", {})] })) : (_jsx("pre", { style: { whiteSpace: 'pre-wrap', wordBreak: 'break-word' }, children: JSON.stringify(orderData, null, 2) }))] }));
}
